// =============================================================================
// ESPOLIDAR WEB ADMIN - DEVICE DETAIL PAGE
// =============================================================================

import { useState, useEffect, useRef, useCallback } from 'react'
import { Terminal as XTerm } from 'xterm'
import { FitAddon } from 'xterm-addon-fit'
import 'xterm/css/xterm.css'
import { useParams, useNavigate } from 'react-router-dom'
import {
  useDevice,
  useUpdateDevice,
  useDeviceZones,
  useCreateZone,
  useUpdateZone,
  useDeleteZone,
  useDeviceTriggers,
  useCreateTrigger,
  useUpdateTrigger,
  useDeleteTrigger,
  useDeviceEventStats,
} from '../hooks/useDevices'
import { useMedia } from '../hooks/useMedia'
import { useWorkspaceConfig, useUpdateWorkspace, useCreateCartesianZone } from '../hooks/useWorkspace'
import { useIdlePlaylist, useUpdateIdlePlaylist, useDeleteIdlePlaylistItem } from '../hooks/useIdlePlaylist'
import { mediaApi } from '../services/api'
// ZoneEditor (polar) removed
import { ZoneEditor2D } from '../components/ZoneEditor2D'
import { ZoneEditor3D } from '../components/ZoneEditor3D'
import ZoneForm from '../components/ZoneForm'
import TriggerForm from '../components/TriggerForm'
// LidarVisualization rimossa - integrata nella Vista 3D
import FileManager from '../components/FileManager'
import Modal from '../components/Modal'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  ArrowLeft,
  Settings,
  Radar,
  Zap,
  BarChart3,
  Plus,
  Edit2,
  Trash2,
  Wifi,
  WifiOff,
  Grid3X3,
  Play,
  Image,
  Video,
  Music,
  GripVertical,
  Terminal,
  Power,
  RotateCcw,
  PowerOff,
  Server,
  AlertTriangle,
  Info,
  HardDrive,
  Cpu as CpuIcon,
  Thermometer,
  Clock,
  Eye,
  BookOpen,
  Cable,
  Lightbulb,
  FolderOpen,
  ScrollText,
  CheckCircle,
  XCircle,
  ArrowDownCircle,
  ArrowUpCircle,
  Activity,
  LogIn,
  LogOut,
  Save,
  RefreshCw,
  Sliders,
  Download,
  Box,
  ChevronUp,
  ChevronDown,
} from 'lucide-react'
import { wsService } from '../services/websocket'
import type { Zone, Trigger, Point2D, WorkspaceConfig } from '../types'

type Tab = 'zones' | 'triggers' | 'idle' | 'events' | 'control' | 'logs' | 'guide' | 'settings'

interface SystemInfo {
  hostname: string
  ip: string
  uptime: string
  disk_usage: string
  memory: string
  cpu_temp: string
  service_status: string
}

interface LogEntry {
  id: number
  timestamp: Date
  type: 'info' | 'success' | 'error' | 'incoming' | 'outgoing'
  message: string
  data?: unknown
}

interface PlayerSettings {
  GPIO_LED_12V_PIN: number
  GPIO_LED_12V_INVERTED: boolean
  GPIO_WS2812_PIN: number
  GPIO_WS2812_COUNT: number
  GPIO_WS2812_BRIGHTNESS: number
  GPIO_RADAR_PIN: number
}

export default function DeviceDetailPage() {
  const { deviceId } = useParams<{ deviceId: string }>()
  const navigate = useNavigate()

  const { data: device, isLoading } = useDevice(deviceId!)
  const { data: zones = [] } = useDeviceZones(deviceId!)
  const { data: triggers = [] } = useDeviceTriggers(deviceId!)
  const { data: mediaList = [] } = useMedia()
  const { data: eventStats } = useDeviceEventStats(deviceId!, 7)

  const updateDevice = useUpdateDevice()
  const createZone = useCreateZone()
  const updateZone = useUpdateZone()
  const deleteZone = useDeleteZone()
  const createTrigger = useCreateTrigger()
  const updateTrigger = useUpdateTrigger()
  const deleteTrigger = useDeleteTrigger()

  // Workspace hooks
  const { data: workspace } = useWorkspaceConfig(deviceId!)
  const updateWorkspace = useUpdateWorkspace()
  const createCartesianZone = useCreateCartesianZone()

  // Idle playlist hooks
  const { data: idlePlaylistRaw = [] } = useIdlePlaylist(deviceId!)
  const updateIdlePlaylist = useUpdateIdlePlaylist()
  const deleteIdlePlaylistItem = useDeleteIdlePlaylistItem()

  // Sort idle playlist by position
  const idlePlaylist = [...idlePlaylistRaw].sort((a, b) => a.position - b.position)

  const [activeTab, setActiveTab] = useState<Tab>('zones')
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null)
  const [zoneModalOpen, setZoneModalOpen] = useState(false)
  const [editingZone, setEditingZone] = useState<Zone | null>(null)
  const [triggerModalOpen, setTriggerModalOpen] = useState(false)
  const [editingTrigger, setEditingTrigger] = useState<Trigger | null>(null)
  const [editorMode, setEditorMode] = useState<'2d' | '3d'>('2d')

  // Settings state
  const [deviceName, setDeviceName] = useState('')
  const [deviceDescription, setDeviceDescription] = useState('')

  // Drag & drop state per idle playlist
  const [draggedItem, setDraggedItem] = useState<number | null>(null)

  // Trigger sorting state
  const [triggerSortBy, setTriggerSortBy] = useState<'zone' | 'type' | 'action' | 'priority'>('zone')
  const [triggerSortDir, setTriggerSortDir] = useState<'asc' | 'desc'>('asc')

  // Sorted triggers
  const sortedTriggers = [...triggers].sort((a, b) => {
    let aVal: string | number = ''
    let bVal: string | number = ''

    switch (triggerSortBy) {
      case 'zone':
        const aZone = zones.find(z => z.id === a.zone_id)
        const bZone = zones.find(z => z.id === b.zone_id)
        aVal = aZone?.name || 'zzz'
        bVal = bZone?.name || 'zzz'
        break
      case 'type':
        aVal = a.trigger_type
        bVal = b.trigger_type
        break
      case 'action':
        aVal = a.action_type
        bVal = b.action_type
        break
      case 'priority':
        aVal = a.priority
        bVal = b.priority
        break
    }

    if (aVal < bVal) return triggerSortDir === 'asc' ? -1 : 1
    if (aVal > bVal) return triggerSortDir === 'asc' ? 1 : -1
    return 0
  })

  // Handle trigger sort
  const handleTriggerSort = (column: 'zone' | 'type' | 'action' | 'priority') => {
    if (triggerSortBy === column) {
      setTriggerSortDir(triggerSortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setTriggerSortBy(column)
      setTriggerSortDir('asc')
    }
  }

  const [dragOverItem, setDragOverItem] = useState<number | null>(null)

  // Handler per drag & drop idle playlist
  const handleIdleDragEnd = () => {
    if (draggedItem === null || dragOverItem === null || draggedItem === dragOverItem) {
      setDraggedItem(null)
      setDragOverItem(null)
      return
    }

    // Riordina la lista
    const newList = [...idlePlaylist]
    const [removed] = newList.splice(draggedItem, 1)

    // Adjust target index when moving to a higher position
    // because the removal shifts all higher indices down by 1
    const targetIndex = draggedItem < dragOverItem ? dragOverItem - 1 : dragOverItem
    newList.splice(targetIndex, 0, removed)

    // Aggiorna le posizioni
    const newItems = newList.map((p, idx) => ({
      media_id: p.media_id,
      position: idx,
      duration: p.duration,
      enabled: p.enabled
    }))

    updateIdlePlaylist.mutate({ deviceId: deviceId!, items: newItems })

    setDraggedItem(null)
    setDragOverItem(null)
  }

  // Remote control state
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null)
  const [newHostname, setNewHostname] = useState('')
  const [isExecuting, setIsExecuting] = useState(false)
  const pendingRequestsRef = useRef<Map<string, (response: unknown) => void>>(new Map())

  // Terminal state
  const terminalRef = useRef<HTMLDivElement>(null)
  const xtermRef = useRef<XTerm | null>(null)
  const fitAddonRef = useRef<FitAddon | null>(null)
  const terminalDataDisposableRef = useRef<{ dispose: () => void } | null>(null)
  const [terminalSessionId, setTerminalSessionId] = useState<string | null>(null)
  const [terminalConnected, setTerminalConnected] = useState(false)

  // LED test state
  const [ledTestColor, setLedTestColor] = useState('#ff0000')
  const [ledTestStart, setLedTestStart] = useState(0)
  const [ledTestEnd, setLedTestEnd] = useState(60)

  // Connection logs state
  const [connectionLogs, setConnectionLogs] = useState<LogEntry[]>([])
  const [isWsConnected, setIsWsConnected] = useState(false)
  const [autoScroll, setAutoScroll] = useState(true)
  const logsEndRef = useRef<HTMLDivElement>(null)
  const logIdRef = useRef(0)

  // LIDAR Radar Trigger state - usa localStorage per persistere lo stato UI
  const [lidarRadarTrigger, setLidarRadarTrigger] = useState(() => {
    if (deviceId) {
      const saved = localStorage.getItem(`lidar_radar_trigger_${deviceId}`)
      return saved === 'true'
    }
    return false
  })

  // Sensor diagnostics state
  interface SensorDiagnostics {
    timestamp: string
    lidar: {
      type: string
      port: string
      baudrate: number
      connected: boolean
      port_exists: boolean
      port_readable: boolean
      device_info?: {
        model?: number
        firmware?: string
        hardware?: number
        serial?: string
        note?: string
      }
      health?: {
        status: string
        status_code?: number
        error_code?: number
        note?: string
      }
      last_scan_points: number
      error?: string
    }
    radar: {
      gpio_pin: number
      gpio_configured: boolean
      readable: boolean
      current_state?: string
      error?: string
    }
    system: {
      handler_running: boolean
      lidar_thread_alive: boolean
      radar_thread_alive: boolean
      zones_configured: number
      rplidar_scanning: boolean
      error?: string
    }
  }
  const [sensorStatus, setSensorStatus] = useState<SensorDiagnostics | null>(null)
  const [isFetchingSensors, setIsFetchingSensors] = useState(false)

  // Player GPIO settings state
  const [playerSettings, setPlayerSettings] = useState<PlayerSettings | null>(null)
  const [editedSettings, setEditedSettings] = useState<Partial<PlayerSettings>>({})
  const [isFetchingSettings, setIsFetchingSettings] = useState(false)
  const [isSavingSettings, setIsSavingSettings] = useState(false)
  const [settingsSaveResult, setSettingsSaveResult] = useState<{ success: boolean; message: string } | null>(null)

  // LIDAR Radar Timeout state - timeout prima di spegnere RPLIDAR dopo uscita radar
  const [lidarRadarTimeout, setLidarRadarTimeout] = useState(() => {
    if (deviceId) {
      const saved = localStorage.getItem(`lidar_radar_timeout_${deviceId}`)
      return saved ? parseFloat(saved) : 5.0
    }
    return 5.0
  })

  // Generate unique request ID
  const generateRequestId = () => `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  // Send command to device via WebSocket
  const sendDeviceCommand = useCallback((command: string, params: Record<string, unknown> = {}) => {
    const requestId = generateRequestId()

    return new Promise<unknown>((resolve) => {
      // Store the resolve function
      pendingRequestsRef.current.set(requestId, resolve)

      // Send command via WebSocket
      wsService.sendCommand(deviceId!, command, { ...params, request_id: requestId })

      // Timeout after 35 seconds
      setTimeout(() => {
        if (pendingRequestsRef.current.has(requestId)) {
          pendingRequestsRef.current.delete(requestId)
          resolve({ error: true, data: 'Timeout: nessuna risposta dal dispositivo' })
        }
      }, 35000)
    })
  }, [deviceId])

  // Handle WebSocket messages for command responses and terminal output
  useEffect(() => {
    const unsubscribe = wsService.subscribe((message) => {
      if (message.type === 'command_response' && message.device_id === deviceId) {
        const response = message.response as { request_id: string; data: unknown; error?: boolean; exit_code?: number }
        const requestId = response.request_id

        const resolver = pendingRequestsRef.current.get(requestId)
        if (resolver) {
          resolver(response)
          pendingRequestsRef.current.delete(requestId)
        }
      } else if (message.type === 'terminal_output' && message.device_id === deviceId) {
        const sessionId = (message as unknown as { session_id: string }).session_id
        const data = (message as unknown as { data: string }).data
        if (sessionId === terminalSessionId && xtermRef.current) {
          xtermRef.current.write(data)
        }
      }
    })

    return () => unsubscribe()
  }, [deviceId, terminalSessionId])

  // Connection logs - subscribe to ALL WebSocket messages for this device
  useEffect(() => {
    const addLog = (type: LogEntry['type'], message: string, data?: unknown) => {
      const newLog: LogEntry = {
        id: ++logIdRef.current,
        timestamp: new Date(),
        type,
        message,
        data
      }
      setConnectionLogs(prev => [...prev.slice(-499), newLog]) // Keep last 500 logs
    }

    // Check WebSocket connection status
    const checkConnection = () => {
      const connected = wsService.isConnected()
      setIsWsConnected(connected)
      if (connected) {
        addLog('success', 'WebSocket connesso al server')
      }
    }

    // Initial check
    checkConnection()

    // Subscribe to WebSocket messages
    const unsubscribe = wsService.subscribe((message) => {
      // Log all messages related to this device
      if (message.device_id === deviceId || !message.device_id) {
        const msgType = message.type || 'unknown'

        switch (msgType) {
          case 'device_connected':
            addLog('success', `Dispositivo connesso`, message)
            break
          case 'device_disconnected':
            addLog('error', `Dispositivo disconnesso`, message)
            break
          case 'device_event':
            addLog('incoming', `Evento: ${(message as { event_type?: string }).event_type || 'unknown'}`, message)
            break
          case 'lidar_scan':
            // Don't log every scan to avoid spam, but log that streaming is active
            break
          case 'heartbeat_ack':
            addLog('incoming', 'Heartbeat ACK ricevuto', message)
            break
          case 'config_update':
            addLog('incoming', `Config update v${(message as { version?: number }).version || '?'}`, message)
            break
          case 'command_response':
            const resp = message.response as { request_id?: string; error?: boolean } | undefined
            if (resp?.error) {
              addLog('error', `Risposta comando (errore): ${resp.request_id}`, message)
            } else {
              addLog('incoming', `Risposta comando: ${resp?.request_id || 'unknown'}`, message)
            }
            break
          case 'terminal_output':
            // Don't log terminal output to avoid spam
            break
          default:
            addLog('incoming', `Messaggio: ${msgType}`, message)
        }
      }
    })

    // Periodic connection check
    const interval = setInterval(checkConnection, 5000)

    return () => {
      unsubscribe()
      clearInterval(interval)
    }
  }, [deviceId])

  // Auto-scroll logs
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [connectionLogs, autoScroll])

  // Helper function to add outgoing log
  const logOutgoing = (message: string, data?: unknown) => {
    const newLog: LogEntry = {
      id: ++logIdRef.current,
      timestamp: new Date(),
      type: 'outgoing',
      message,
      data
    }
    setConnectionLogs(prev => [...prev.slice(-499), newLog])
  }

  // Terminal initialization
  const initTerminal = useCallback(() => {
    if (!terminalRef.current || xtermRef.current) return

    const term = new XTerm({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1a1a2e',
        foreground: '#eaeaea',
        cursor: '#f0f0f0',
        cursorAccent: '#1a1a2e',
        selectionBackground: '#5a5a8a',
      },
      rows: 20,
      cols: 80,
    })

    const fitAddon = new FitAddon()
    term.loadAddon(fitAddon)

    term.open(terminalRef.current)
    fitAddon.fit()

    xtermRef.current = term
    fitAddonRef.current = fitAddon

    // Handle window resize
    const handleResize = () => {
      if (fitAddonRef.current && xtermRef.current) {
        fitAddonRef.current.fit()
        if (terminalSessionId) {
          wsService.sendCommand(deviceId!, 'terminal_resize', {
            session_id: terminalSessionId,
            cols: xtermRef.current.cols,
            rows: xtermRef.current.rows,
          })
        }
      }
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [deviceId, terminalSessionId])

  // Connect terminal
  const connectTerminal = useCallback(() => {
    if (!xtermRef.current) {
      initTerminal()
    }

    const sessionId = `term_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setTerminalSessionId(sessionId)

    // Send terminal_open command
    wsService.sendCommand(deviceId!, 'terminal_open', { session_id: sessionId })

    // Handle terminal input - dispose previous listener if exists
    if (xtermRef.current) {
      if (terminalDataDisposableRef.current) {
        terminalDataDisposableRef.current.dispose()
      }
      terminalDataDisposableRef.current = xtermRef.current.onData((data) => {
        wsService.sendCommand(deviceId!, 'terminal_input', {
          session_id: sessionId,
          data: data,
        })
      })

      // Send initial resize
      setTimeout(() => {
        if (fitAddonRef.current) {
          fitAddonRef.current.fit()
        }
        if (xtermRef.current) {
          wsService.sendCommand(deviceId!, 'terminal_resize', {
            session_id: sessionId,
            cols: xtermRef.current.cols,
            rows: xtermRef.current.rows,
          })
        }
      }, 100)
    }

    setTerminalConnected(true)
  }, [deviceId, initTerminal])

  // Disconnect terminal
  const disconnectTerminal = useCallback(() => {
    if (terminalSessionId) {
      wsService.sendCommand(deviceId!, 'terminal_close', { session_id: terminalSessionId })
      setTerminalSessionId(null)
      setTerminalConnected(false)
      // Dispose input listener to prevent duplicate handlers
      if (terminalDataDisposableRef.current) {
        terminalDataDisposableRef.current.dispose()
        terminalDataDisposableRef.current = null
      }
      if (xtermRef.current) {
        xtermRef.current.write('\r\n\x1b[33m[Disconnesso]\x1b[0m\r\n')
      }
    }
  }, [deviceId, terminalSessionId])

  // Cleanup terminal on unmount or tab change
  useEffect(() => {
    return () => {
      if (terminalSessionId) {
        wsService.sendCommand(deviceId!, 'terminal_close', { session_id: terminalSessionId })
      }
      if (terminalDataDisposableRef.current) {
        terminalDataDisposableRef.current.dispose()
        terminalDataDisposableRef.current = null
      }
      if (xtermRef.current) {
        xtermRef.current.dispose()
        xtermRef.current = null
      }
    }
  }, [])

  // System actions
  const handleReboot = async () => {
    if (!confirm('Riavviare il dispositivo? Il dispositivo sarà offline per alcuni minuti.')) return

    try {
      await sendDeviceCommand('system_reboot')
      alert('Comando di riavvio inviato. Il dispositivo sarà offline per alcuni minuti.')
    } catch (error) {
      alert(`Errore: ${error}`)
    }
  }

  const handleShutdown = async () => {
    if (!confirm('ATTENZIONE: Spegnere il dispositivo? Sarà necessario accenderlo fisicamente per riavviarlo.')) return

    try {
      await sendDeviceCommand('system_shutdown')
      alert('Comando di spegnimento inviato.')
    } catch (error) {
      alert(`Errore: ${error}`)
    }
  }

  const handleSetHostname = async () => {
    if (!newHostname.trim()) return
    if (!confirm(`Cambiare il nome del dispositivo in "${newHostname}"?`)) return

    setIsExecuting(true)

    try {
      const response = await sendDeviceCommand('system_set_hostname', { hostname: newHostname.trim() }) as { data: string; error?: boolean }

      if (response.error) {
        alert(`Errore: ${response.data}`)
      } else {
        alert(`Hostname cambiato con successo. Riavviare il dispositivo per applicare.`)
        setNewHostname('')
        fetchSystemInfo()
      }
    } catch (error) {
      alert(`Errore: ${error}`)
    } finally {
      setIsExecuting(false)
    }
  }

  const fetchSystemInfo = async () => {
    setIsExecuting(true)

    try {
      const response = await sendDeviceCommand('system_get_info') as { data: SystemInfo; error?: boolean }

      if (response.error) {
        console.error('Failed to fetch system info:', response.data)
      } else {
        setSystemInfo(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch system info:', error)
    } finally {
      setIsExecuting(false)
    }
  }

  // Fetch sensor status
  const fetchSensorStatus = async () => {
    setIsFetchingSensors(true)
    logOutgoing('Richiesta diagnostica sensori', { command: 'get_sensor_diagnostics' })

    try {
      const response = await sendDeviceCommand('get_sensor_diagnostics') as { data: SensorDiagnostics; error?: boolean }

      if (response.error) {
        console.error('Failed to fetch sensor diagnostics:', response.data)
      } else {
        setSensorStatus(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch sensor diagnostics:', error)
    } finally {
      setIsFetchingSensors(false)
    }
  }

  // LED test handlers
  const handleLedTest = (color: string) => {
    // Convert hex color to RGB integer
    const hex = color.replace('#', '')
    const colorInt = parseInt(hex, 16)

    // Invia comando senza aspettare risposta
    wsService.sendCommand(deviceId!, 'led_ws2812_set', {
      start: ledTestStart,
      end: ledTestEnd,
      color: colorInt
    })
    console.log('LED command sent:', { color: colorInt, start: ledTestStart, end: ledTestEnd })
  }

  const handleLedOff = () => {
    wsService.sendCommand(deviceId!, 'led_ws2812_set', {
      start: 0,
      end: 300,
      color: 0x000000
    })
    console.log('LED off command sent')
  }

  // LIDAR Radar Trigger handlers
  const handleLidarRadarTriggerToggle = (enabled: boolean) => {
    console.log('Toggle LIDAR radar trigger:', enabled)
    // Invia comando senza aspettare risposta (come i comandi LED)
    wsService.sendCommand(deviceId!, 'set_lidar_radar_trigger', { enabled })
    // Aggiorna UI e salva in localStorage
    setLidarRadarTrigger(enabled)
    if (deviceId) {
      localStorage.setItem(`lidar_radar_trigger_${deviceId}`, String(enabled))
    }
    console.log('LIDAR radar trigger command sent, UI updated to:', enabled)
  }

  // LIDAR Radar Timeout handler
  const handleLidarRadarTimeoutChange = (timeout: number) => {
    console.log('Setting LIDAR radar timeout:', timeout)
    // Invia comando senza aspettare risposta
    wsService.sendCommand(deviceId!, 'set_lidar_radar_timeout', { timeout })
    // Aggiorna UI e salva in localStorage
    setLidarRadarTimeout(timeout)
    if (deviceId) {
      localStorage.setItem(`lidar_radar_timeout_${deviceId}`, String(timeout))
    }
    console.log('LIDAR radar timeout command sent:', timeout)
  }

  // Player GPIO settings handlers
  const fetchPlayerSettings = async () => {
    setIsFetchingSettings(true)
    setSettingsSaveResult(null)
    try {
      const response = await sendDeviceCommand('get_player_settings') as { data: { settings: PlayerSettings }; error?: boolean }
      if (response.error) {
        console.error('Failed to fetch player settings:', response.data)
      } else {
        setPlayerSettings(response.data.settings)
        setEditedSettings({})
      }
    } catch (error) {
      console.error('Failed to fetch player settings:', error)
    } finally {
      setIsFetchingSettings(false)
    }
  }

  const getSettingValue = <K extends keyof PlayerSettings>(key: K): PlayerSettings[K] | undefined => {
    if (key in editedSettings) return editedSettings[key] as PlayerSettings[K]
    if (playerSettings) return playerSettings[key]
    return undefined
  }

  const updateSetting = (key: keyof PlayerSettings, value: PlayerSettings[keyof PlayerSettings]) => {
    setEditedSettings(prev => {
      // If same as original, remove from edited
      if (playerSettings && playerSettings[key] === value) {
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: value }
    })
    setSettingsSaveResult(null)
  }

  const hasSettingsChanges = Object.keys(editedSettings).length > 0

  const handleSavePlayerSettings = async (autoRestart: boolean) => {
    if (!hasSettingsChanges) return

    const action = autoRestart
      ? 'Salvare le impostazioni e riavviare il servizio player?'
      : 'Salvare le impostazioni? Il riavvio sarà necessario per applicarle.'
    if (!confirm(action)) return

    setIsSavingSettings(true)
    setSettingsSaveResult(null)
    try {
      const response = await sendDeviceCommand('update_player_settings', {
        settings: editedSettings,
        auto_restart: autoRestart
      }) as { data: { updated: string[]; errors: string[]; restart_required: boolean }; error?: boolean }

      if (response.error) {
        setSettingsSaveResult({ success: false, message: String(response.data) })
      } else {
        const { updated, errors } = response.data
        if (errors.length > 0) {
          setSettingsSaveResult({ success: false, message: `Errori: ${errors.join(', ')}` })
        } else {
          setSettingsSaveResult({
            success: true,
            message: autoRestart
              ? `${updated.length} parametri salvati. Riavvio in corso...`
              : `${updated.length} parametri salvati. Riavvio necessario per applicare.`
          })
          // Merge edited into playerSettings
          if (playerSettings) {
            setPlayerSettings({ ...playerSettings, ...editedSettings } as PlayerSettings)
          }
          setEditedSettings({})
        }
      }
    } catch (error) {
      setSettingsSaveResult({ success: false, message: `Errore: ${error}` })
    } finally {
      setIsSavingSettings(false)
    }
  }

  // Fetch system info when control tab is opened
  useEffect(() => {
    if (activeTab === 'control' && device?.status === 'online') {
      fetchSystemInfo()
    }
  }, [activeTab, device?.status])

  // Default workspace if not configured
  const defaultWorkspace: WorkspaceConfig = {
    id: 0,
    device_id: 0,
    width: 10000,
    height: 8000,
    lidar_x: 5000,
    lidar_y: 0,
    lidar_rotation: 0,
    lidar_tilt: 0
  }

  const currentWorkspace = workspace || defaultWorkspace

  // Handlers for 2D editor
  const handleCreateZone2D = (points: Point2D[], name: string) => {
    const geometryType = points.length === 4 ? 'rectangle' : 'polygon'
    createCartesianZone.mutate({
      deviceId: deviceId!,
      data: {
        zone_number: zones.length + 1,
        name,
        geometry_type: geometryType,
        geometry_points: points,
        enabled: true
      }
    })
  }

  const handleUpdateZone2D = (zone: Zone) => {
    updateZone.mutate({
      zoneId: zone.id,
      data: {
        geometry_points: zone.geometry_points
      },
      deviceId: deviceId!
    })
  }

  const handleUpdateWorkspace = (partial: Partial<WorkspaceConfig>) => {
    updateWorkspace.mutate({
      deviceId: deviceId!,
      data: partial
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!device) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Dispositivo non trovato</p>
        <button onClick={() => navigate('/devices')} className="btn-primary mt-4">
          Torna ai dispositivi
        </button>
      </div>
    )
  }

  const handleSaveZone = (data: Zone | Partial<Zone>) => {
    if (editingZone) {
      updateZone.mutate({ zoneId: editingZone.id, data, deviceId: deviceId! })
    } else {
      createZone.mutate({ deviceId: deviceId!, data: data as Zone })
    }
    setZoneModalOpen(false)
    setEditingZone(null)
  }

  const handleSaveTrigger = (data: Trigger | Partial<Trigger>) => {
    if (editingTrigger) {
      updateTrigger.mutate({ triggerId: editingTrigger.id, data, deviceId: deviceId! })
    } else {
      createTrigger.mutate({ deviceId: deviceId!, data: data as Trigger })
    }
    setTriggerModalOpen(false)
    setEditingTrigger(null)
  }

  const handleDeleteTrigger = (triggerId: number) => {
    if (confirm('Eliminare questo trigger?')) {
      deleteTrigger.mutate({ triggerId, deviceId: deviceId! })
    }
  }

  const handleSaveSettings = () => {
    updateDevice.mutate({
      deviceId: deviceId!,
      data: { name: deviceName, description: deviceDescription },
    })
  }

  const tabs = [
    { id: 'zones' as Tab, label: 'Zone LIDAR', icon: Radar },
    { id: 'triggers' as Tab, label: 'Trigger', icon: Zap },
    { id: 'idle' as Tab, label: 'Contenuti Idle', icon: Play },
    { id: 'events' as Tab, label: 'Eventi', icon: BarChart3 },
    { id: 'control' as Tab, label: 'Controllo', icon: Terminal },
    { id: 'logs' as Tab, label: 'Log Connessione', icon: ScrollText },
    { id: 'guide' as Tab, label: 'Guida Hardware', icon: BookOpen },
    { id: 'settings' as Tab, label: 'Impostazioni', icon: Settings },
  ]

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/devices')}
          className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{device.name}</h1>
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                device.status === 'online'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {device.status === 'online' ? <Wifi size={12} /> : <WifiOff size={12} />}
              {device.status}
            </span>
          </div>
          <p className="text-sm text-gray-500">{device.device_id}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'zones' && (
        <div className="space-y-6">
          {/* Editor mode toggle */}
          <div className="flex items-center gap-4 bg-white rounded-xl shadow-sm p-4">
            <span className="text-sm font-medium text-gray-700">Modalità editor:</span>
            <div className="flex rounded-lg bg-gray-100 p-1">
              <button
                onClick={() => setEditorMode('2d')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  editorMode === '2d'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Grid3X3 size={16} />
                Editor 2D
              </button>
              <button
                onClick={() => setEditorMode('3d')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  editorMode === '3d'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Box size={16} />
                Vista 3D
              </button>
            </div>
          </div>

          {editorMode === '3d' && (
            /* 3D View Mode */
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 bg-gray-800 rounded-xl shadow-sm overflow-hidden" style={{ height: '600px' }}>
                <ZoneEditor3D
                  zones={zones.filter(z => z.geometry_points && z.geometry_points.length >= 3)}
                  workspace={currentWorkspace}
                  selectedZone={selectedZone}
                  onSelectZone={setSelectedZone}
                  deviceId={deviceId}
                  isDeviceOnline={device?.status === 'online'}
                />
              </div>
              {/* Zone list */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Zone ({zones.length})</h3>
                  <div className="space-y-2">
                    {zones.map(zone => (
                      <div
                        key={zone.id}
                        onClick={() => setSelectedZone(selectedZone?.id === zone.id ? null : zone)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedZone?.id === zone.id
                            ? 'bg-blue-50 border border-blue-200'
                            : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{
                                backgroundColor: zone.led_color
                                  ? `rgb(${zone.led_color[0]},${zone.led_color[1]},${zone.led_color[2]})`
                                  : '#888'
                              }}
                            />
                            <span className="font-medium text-sm">{zone.name}</span>
                          </div>
                          <span className={`text-xs ${zone.enabled ? 'text-green-600' : 'text-gray-400'}`}>
                            {zone.enabled ? 'ON' : 'OFF'}
                          </span>
                        </div>
                      </div>
                    ))}
                    {zones.length === 0 && (
                      <p className="text-sm text-gray-500 italic">Nessuna zona configurata</p>
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Info Workspace</h3>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>Area: {currentWorkspace.width}mm × {currentWorkspace.height}mm</p>
                    <p>LIDAR: ({currentWorkspace.lidar_x}, {currentWorkspace.lidar_y})mm</p>
                    <p>Rotazione: {currentWorkspace.lidar_rotation}°</p>
                    <p>Tilt: {currentWorkspace.lidar_tilt}°</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {editorMode === '2d' && (
            /* 2D Editor Mode */
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* 2D Zone Editor - takes 2 columns */}
              <div className="xl:col-span-2 bg-gray-800 rounded-xl shadow-sm overflow-hidden" style={{ height: '600px' }}>
                <ZoneEditor2D
                  deviceId={deviceId!}
                  zones={zones.filter(z => z.geometry_points && z.geometry_points.length >= 3)}
                  workspace={currentWorkspace}
                  onCreateZone={handleCreateZone2D}
                  onUpdateZone={handleUpdateZone2D}
                  onDeleteZone={(zoneId) => {
                    if (confirm('Eliminare questa zona?')) {
                      deleteZone.mutate({ zoneId, deviceId: deviceId! })
                      setSelectedZone(null)
                    }
                  }}
                  onSelectZone={setSelectedZone}
                  onUpdateWorkspace={handleUpdateWorkspace}
                  selectedZone={selectedZone}
                />
              </div>

              {/* Zone list & Workspace settings */}
              <div className="space-y-6">
                {/* Workspace Settings */}
                <div className="bg-white rounded-xl shadow-sm p-6" key={`workspace-${workspace?.id || 'default'}-${workspace?.width}-${workspace?.height}`}>
                  <h2 className="text-lg font-semibold mb-4">Area di Lavoro</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Larghezza (mm)</label>
                      <input
                        type="number"
                        defaultValue={currentWorkspace.width}
                        onBlur={(e) => {
                          const val = Math.max(100, Math.min(50000, parseInt(e.target.value) || 10000))
                          e.target.value = val.toString()
                          if (val !== currentWorkspace.width) handleUpdateWorkspace({ width: val })
                        }}
                        className="input"
                        min={100}
                        max={50000}
                        step={100}
                      />
                      <span className="text-xs text-gray-500">Min: 100mm (10cm)</span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Altezza (mm)</label>
                      <input
                        type="number"
                        defaultValue={currentWorkspace.height}
                        onBlur={(e) => {
                          const val = Math.max(100, Math.min(50000, parseInt(e.target.value) || 8000))
                          e.target.value = val.toString()
                          if (val !== currentWorkspace.height) handleUpdateWorkspace({ height: val })
                        }}
                        className="input"
                        min={100}
                        max={50000}
                        step={100}
                      />
                      <span className="text-xs text-gray-500">Min: 100mm (10cm)</span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">LIDAR X (mm)</label>
                      <input
                        type="number"
                        defaultValue={currentWorkspace.lidar_x}
                        onBlur={(e) => {
                          const val = parseInt(e.target.value) || 0
                          if (val !== currentWorkspace.lidar_x) handleUpdateWorkspace({ lidar_x: val })
                        }}
                        className="input"
                        step={100}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">LIDAR Y (mm)</label>
                      <input
                        type="number"
                        defaultValue={currentWorkspace.lidar_y}
                        onBlur={(e) => {
                          const val = parseInt(e.target.value) || 0
                          if (val !== currentWorkspace.lidar_y) handleUpdateWorkspace({ lidar_y: val })
                        }}
                        className="input"
                        step={100}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rotazione LIDAR (gradi)
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          id="rotation-range"
                          min={0}
                          max={360}
                          step={5}
                          defaultValue={currentWorkspace.lidar_rotation}
                          onChange={(e) => {
                            const val = parseInt(e.target.value)
                            const numInput = document.getElementById('rotation-number') as HTMLInputElement
                            if (numInput) numInput.value = val.toString()
                            handleUpdateWorkspace({ lidar_rotation: val })
                          }}
                          className="flex-1"
                        />
                        <input
                          type="number"
                          id="rotation-number"
                          defaultValue={currentWorkspace.lidar_rotation}
                          onBlur={(e) => {
                            let val = parseInt(e.target.value) || 0
                            val = ((val % 360) + 360) % 360 // Normalizza 0-360
                            e.target.value = val.toString()
                            const rangeInput = document.getElementById('rotation-range') as HTMLInputElement
                            if (rangeInput) rangeInput.value = val.toString()
                            if (val !== currentWorkspace.lidar_rotation) handleUpdateWorkspace({ lidar_rotation: val })
                          }}
                          className="input w-20"
                          min={0}
                          max={360}
                        />
                        <span className="text-gray-500">°</span>
                      </div>
                      <span className="text-xs text-gray-500">0° = freccia verso l'alto, 90° = verso destra</span>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Inclinazione LIDAR (gradi)
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          id="tilt-range"
                          min={-45}
                          max={45}
                          step={1}
                          defaultValue={currentWorkspace.lidar_tilt}
                          onChange={(e) => {
                            const val = parseInt(e.target.value)
                            const numInput = document.getElementById('tilt-number') as HTMLInputElement
                            if (numInput) numInput.value = val.toString()
                            handleUpdateWorkspace({ lidar_tilt: val })
                          }}
                          className="flex-1"
                        />
                        <input
                          type="number"
                          id="tilt-number"
                          defaultValue={currentWorkspace.lidar_tilt}
                          onBlur={(e) => {
                            let val = parseInt(e.target.value) || 0
                            val = Math.max(-90, Math.min(90, val))
                            e.target.value = val.toString()
                            const rangeInput = document.getElementById('tilt-range') as HTMLInputElement
                            if (rangeInput) rangeInput.value = Math.max(-45, Math.min(45, val)).toString()
                            if (val !== currentWorkspace.lidar_tilt) handleUpdateWorkspace({ lidar_tilt: val })
                          }}
                          className="input w-20"
                          min={-90}
                          max={90}
                        />
                        <span className="text-gray-500">°</span>
                      </div>
                      <span className="text-xs text-gray-500">0° = orizzontale, positivo = verso l'alto, negativo = verso il basso</span>
                    </div>
                  </div>
                </div>

                {/* Radar Simulation */}
                <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
                  <h2 className="text-lg font-semibold mb-3">Simulazione Radar</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => wsService.sendCommand(deviceId!, 'simulate_radar', { state: 'ENTER' })}
                      className="flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                    >
                      <LogIn size={16} />
                      <span className="text-sm font-medium">Ingresso Radar</span>
                    </button>
                    <button
                      onClick={() => wsService.sendCommand(deviceId!, 'simulate_radar', { state: 'EXIT' })}
                      className="flex items-center gap-2 px-3 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                    >
                      <LogOut size={16} />
                      <span className="text-sm font-medium">Uscita Radar</span>
                    </button>
                  </div>
                </div>

                {/* Zone list */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-semibold mb-4">Zone ({zones.length})</h2>
                  {zones.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {zones.map((zone) => (
                        <div
                          key={zone.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedZone?.id === zone.id
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setSelectedZone(zone)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: `rgb(${zone.led_color.join(',')})` }}
                              />
                              <span className="font-medium text-sm">{zone.name}</span>
                              {!zone.enabled && (
                                <span className="text-xs text-gray-400">(off)</span>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  wsService.sendCommand(deviceId!, 'simulate_zone', { zone_id: zone.id, state: 'ENTER' })
                                }}
                                className="p-1 text-gray-400 hover:text-green-600"
                                title="Simula INGRESSO zona"
                              >
                                <LogIn size={14} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  wsService.sendCommand(deviceId!, 'simulate_zone', { zone_id: zone.id, state: 'EXIT' })
                                }}
                                className="p-1 text-gray-400 hover:text-orange-600"
                                title="Simula USCITA zona"
                              >
                                <LogOut size={14} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setEditingZone(zone)
                                  setZoneModalOpen(true)
                                }}
                                className="p-1 text-gray-400 hover:text-primary-600"
                                title="Modifica zona"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (confirm('Eliminare questa zona?')) {
                                    deleteZone.mutate({ zoneId: zone.id, deviceId: deviceId! })
                                    setSelectedZone(null)
                                  }
                                }}
                                className="p-1 text-gray-400 hover:text-red-600"
                                title="Elimina zona"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4 text-sm">
                      Disegna zone nell'editor
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      {activeTab === 'triggers' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold">Trigger Configurati</h2>
            <button
              onClick={() => {
                setEditingTrigger(null)
                setTriggerModalOpen(true)
              }}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={16} />
              Nuovo Trigger
            </button>
          </div>

          {triggers.length > 0 ? (
            <table className="table">
              <thead>
                <tr>
                  <th
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleTriggerSort('type')}
                  >
                    <div className="flex items-center gap-1">
                      Tipo
                      {triggerSortBy === 'type' && (
                        triggerSortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleTriggerSort('zone')}
                  >
                    <div className="flex items-center gap-1">
                      Zona
                      {triggerSortBy === 'zone' && (
                        triggerSortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleTriggerSort('action')}
                  >
                    <div className="flex items-center gap-1">
                      Azione
                      {triggerSortBy === 'action' && (
                        triggerSortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                  <th>Delay</th>
                  <th>Durata</th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleTriggerSort('priority')}
                  >
                    <div className="flex items-center gap-1">
                      Priorità
                      {triggerSortBy === 'priority' && (
                        triggerSortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                  <th>Stato</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedTriggers.map((trigger) => (
                  <tr key={trigger.id}>
                    <td>
                      <span className="font-medium">
                        {trigger.trigger_type.replace('_', ' → ')}
                      </span>
                    </td>
                    <td>
                      {trigger.zone_id
                        ? zones.find((z) => z.id === trigger.zone_id)?.name || '-'
                        : '-'}
                    </td>
                    <td>
                      <span className="text-gray-600">{trigger.action_type}</span>
                      {trigger.media_id && (
                        <span className="text-xs text-gray-400 block">
                          Media ID: {trigger.media_id}
                        </span>
                      )}
                    </td>
                    <td>
                      {trigger.activation_delay > 0 ? (
                        <span className="text-amber-600 font-medium">{trigger.activation_delay}s</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td>
                      {trigger.duration > 0 ? (
                        <span className="text-blue-600 font-medium">{trigger.duration}s</span>
                      ) : (
                        <span className="text-gray-400">auto</span>
                      )}
                    </td>
                    <td>{trigger.priority}</td>
                    <td>
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          trigger.enabled
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {trigger.enabled ? 'Attivo' : 'Disattivo'}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => {
                            setEditingTrigger(trigger)
                            setTriggerModalOpen(true)
                          }}
                          className="p-1 text-gray-400 hover:text-primary-600"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteTrigger(trigger.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500 text-center py-8">
              Nessun trigger configurato. Clicca "Nuovo Trigger" per iniziare.
            </p>
          )}
        </div>
      )}

      {activeTab === 'idle' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold">Contenuti Idle</h2>
              <p className="text-sm text-gray-500 mt-1">
                Contenuti visualizzati quando non ci sono eventi attivi
              </p>
            </div>
          </div>

          {/* Add media selector */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Aggiungi Media alla Playlist</h3>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">Seleziona Media</label>
                <select
                  id="idle-media-select"
                  className="input"
                  defaultValue=""
                >
                  <option value="">-- Seleziona un file --</option>
                  {mediaList
                    .filter(m => !idlePlaylist.find(p => p.media_id === m.id))
                    .map(media => (
                      <option key={media.id} value={media.id}>
                        {media.name} ({media.media_type})
                      </option>
                    ))}
                </select>
              </div>
              <div className="w-32">
                <label className="block text-sm text-gray-600 mb-1">Durata (sec)</label>
                <input
                  id="idle-duration-input"
                  type="number"
                  className="input"
                  defaultValue={10}
                  min={1}
                  max={3600}
                />
              </div>
              <button
                onClick={() => {
                  const selectEl = document.getElementById('idle-media-select') as HTMLSelectElement
                  const durationEl = document.getElementById('idle-duration-input') as HTMLInputElement
                  const mediaId = parseInt(selectEl.value)
                  const duration = parseInt(durationEl.value) || 10

                  if (mediaId) {
                    const newItems = [
                      ...idlePlaylist.map(p => ({
                        media_id: p.media_id,
                        position: p.position,
                        duration: p.duration,
                        enabled: p.enabled
                      })),
                      {
                        media_id: mediaId,
                        position: idlePlaylist.length,
                        duration: duration,
                        enabled: true
                      }
                    ]
                    updateIdlePlaylist.mutate({ deviceId: deviceId!, items: newItems })
                    selectEl.value = ''
                  }
                }}
                className="btn-primary flex items-center gap-2"
                disabled={updateIdlePlaylist.isPending}
              >
                <Plus size={16} />
                Aggiungi
              </button>
            </div>
          </div>

          {/* Playlist items */}
          {idlePlaylist.length > 0 ? (
            <div className="space-y-2">
              {idlePlaylist.map((item, index) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={() => setDraggedItem(index)}
                  onDragEnter={() => setDragOverItem(index)}
                  onDragEnd={handleIdleDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                  className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all cursor-move ${
                    draggedItem === index
                      ? 'opacity-50 border-primary-400 bg-primary-50'
                      : dragOverItem === index
                      ? 'border-primary-400 bg-primary-50'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  <div className="text-gray-400 cursor-grab active:cursor-grabbing">
                    <GripVertical size={20} />
                  </div>
                  <div className="w-12 text-center text-sm font-medium text-gray-500">
                    #{index + 1}
                  </div>

                  {/* Preview thumbnail */}
                  <div className="w-20 h-14 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                    {item.media?.media_type === 'image' && item.media.filename && (
                      <img
                        src={mediaApi.getPreviewUrl(item.media.filename)}
                        alt={item.media.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {item.media?.media_type === 'video' && item.media.filename && (
                      <video
                        src={mediaApi.getPreviewUrl(item.media.filename)}
                        className="w-full h-full object-cover"
                        muted
                      />
                    )}
                    {item.media?.media_type === 'audio' && (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music size={24} className="text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Media info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {item.media?.name || `Media #${item.media_id}`}
                    </p>
                    <p className="text-sm text-gray-500 flex items-center gap-2">
                      {item.media?.media_type === 'video' && <Video size={14} />}
                      {item.media?.media_type === 'image' && <Image size={14} />}
                      {item.media?.media_type === 'audio' && <Music size={14} />}
                      {item.media?.media_type}
                    </p>
                  </div>

                  {/* Duration */}
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={item.duration}
                      onChange={(e) => {
                        const newDuration = parseInt(e.target.value) || 10
                        const newItems = idlePlaylist.map(p => ({
                          media_id: p.media_id,
                          position: p.position,
                          duration: p.id === item.id ? newDuration : p.duration,
                          enabled: p.enabled
                        }))
                        updateIdlePlaylist.mutate({ deviceId: deviceId!, items: newItems })
                      }}
                      className="input w-20 text-center"
                      min={1}
                      max={3600}
                    />
                    <span className="text-sm text-gray-500">sec</span>
                  </div>

                  {/* Delete */}
                  <button
                    onClick={() => {
                      if (confirm('Rimuovere questo elemento dalla playlist?')) {
                        deleteIdlePlaylistItem.mutate({ deviceId: deviceId!, itemId: item.id })
                      }
                    }}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Play size={32} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun contenuto idle</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Aggiungi immagini o video che verranno visualizzati in loop quando non ci sono eventi attivi.
              </p>
            </div>
          )}

          {/* Info box */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Come funziona:</strong> Questi contenuti verranno riprodotti in sequenza quando il player
              è in stato idle (nessun evento trigger attivo). Le immagini vengono mostrate per la durata specificata,
              i video vengono riprodotti per intero.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'events' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-6">Statistiche Eventi (7 giorni)</h2>

          {eventStats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-primary-600">
                  {eventStats.total_events}
                </p>
                <p className="text-sm text-gray-500">Eventi Totali</p>
              </div>
              {Object.entries(eventStats.by_type).map(([type, count]) => (
                <div key={type} className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-sm text-gray-500">{type}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Nessun evento registrato</p>
          )}
        </div>
      )}

      {activeTab === 'control' && (
        <div className="space-y-6">
          {/* Check if device is online */}
          {device.status !== 'online' ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
              <AlertTriangle size={48} className="text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">Dispositivo Offline</h3>
              <p className="text-yellow-700">
                Il controllo remoto è disponibile solo quando il dispositivo è online.
              </p>
            </div>
          ) : (
            <>
              {/* System Info */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Info size={20} className="text-primary-600" />
                    Informazioni Sistema
                  </h2>
                  <button
                    onClick={fetchSystemInfo}
                    disabled={isExecuting}
                    className="btn btn-secondary text-sm flex items-center gap-2"
                  >
                    <RotateCcw size={14} className={isExecuting ? 'animate-spin' : ''} />
                    Aggiorna
                  </button>
                </div>

                {systemInfo ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <Server size={14} />
                        <span className="text-xs">Hostname</span>
                      </div>
                      <p className="font-semibold text-gray-900">{systemInfo.hostname}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <Wifi size={14} />
                        <span className="text-xs">IP</span>
                      </div>
                      <p className="font-semibold text-gray-900 font-mono text-sm">{systemInfo.ip}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <Clock size={14} />
                        <span className="text-xs">Uptime</span>
                      </div>
                      <p className="font-semibold text-gray-900 text-sm">{systemInfo.uptime}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <Thermometer size={14} />
                        <span className="text-xs">CPU Temp</span>
                      </div>
                      <p className="font-semibold text-gray-900">{systemInfo.cpu_temp}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg col-span-2">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <HardDrive size={14} />
                        <span className="text-xs">Disco</span>
                      </div>
                      <p className="font-mono text-xs text-gray-900">{systemInfo.disk_usage}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg col-span-2">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <CpuIcon size={14} />
                        <span className="text-xs">Memoria</span>
                      </div>
                      <p className="font-mono text-xs text-gray-900">{systemInfo.memory}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {isExecuting ? (
                      <div className="flex items-center justify-center gap-2">
                        <LoadingSpinner size="sm" />
                        <span>Caricamento...</span>
                      </div>
                    ) : (
                      'Clicca "Aggiorna" per ottenere le informazioni di sistema'
                    )}
                  </div>
                )}
              </div>

              {/* Sensor Diagnostics */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Radar size={20} className="text-primary-600" />
                    Diagnostica Sensori
                  </h2>
                  <button
                    onClick={fetchSensorStatus}
                    disabled={isFetchingSensors}
                    className="btn btn-secondary text-sm flex items-center gap-2"
                  >
                    <RotateCcw size={14} className={isFetchingSensors ? 'animate-spin' : ''} />
                    Verifica Sensori
                  </button>
                </div>

                {sensorStatus ? (
                  <div className="space-y-6">
                    {/* Timestamp */}
                    <p className="text-xs text-gray-500">
                      Ultimo test: {sensorStatus.timestamp}
                    </p>

                    {/* Hardware Status Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* LIDAR Hardware */}
                      <div className={`p-4 rounded-lg border-2 ${
                        sensorStatus.lidar.connected
                          ? 'bg-green-50 border-green-300'
                          : 'bg-red-50 border-red-300'
                      }`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Radar size={24} className={
                              sensorStatus.lidar.connected ? 'text-green-600' : 'text-red-600'
                            } />
                            <span className="font-semibold text-lg">LIDAR</span>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            sensorStatus.lidar.connected
                              ? 'bg-green-200 text-green-800'
                              : 'bg-red-200 text-red-800'
                          }`}>
                            {sensorStatus.lidar.connected ? '✓ CONNESSO' : '✗ NON CONNESSO'}
                          </span>
                        </div>

                        <dl className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Tipo:</dt>
                            <dd className="font-mono">{sensorStatus.lidar.type}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Porta:</dt>
                            <dd className="font-mono">{sensorStatus.lidar.port}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Baudrate:</dt>
                            <dd className="font-mono">{sensorStatus.lidar.baudrate}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Porta esiste:</dt>
                            <dd>{sensorStatus.lidar.port_exists ? '✓ Sì' : '✗ No'}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Scansione attiva:</dt>
                            <dd>{sensorStatus.system.rplidar_scanning ? '✓ Sì' : '○ No'}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Punti ultima scan:</dt>
                            <dd className="font-mono">{sensorStatus.lidar.last_scan_points}</dd>
                          </div>
                        </dl>

                        {/* Device Info */}
                        {sensorStatus.lidar.device_info && (
                          <div className="mt-3 pt-3 border-t border-green-200">
                            <p className="text-xs font-medium text-green-700 mb-1">Info Dispositivo</p>
                            {sensorStatus.lidar.device_info.note ? (
                              <p className="text-xs text-gray-500 italic">{sensorStatus.lidar.device_info.note}</p>
                            ) : (
                              <dl className="space-y-1 text-xs">
                                {sensorStatus.lidar.device_info.model !== undefined && (
                                  <div className="flex justify-between">
                                    <dt className="text-gray-500">Modello:</dt>
                                    <dd className="font-mono">{sensorStatus.lidar.device_info.model}</dd>
                                  </div>
                                )}
                                {sensorStatus.lidar.device_info.firmware && (
                                  <div className="flex justify-between">
                                    <dt className="text-gray-500">Firmware:</dt>
                                    <dd className="font-mono">{sensorStatus.lidar.device_info.firmware}</dd>
                                  </div>
                                )}
                                {sensorStatus.lidar.device_info.hardware !== undefined && (
                                  <div className="flex justify-between">
                                    <dt className="text-gray-500">Hardware:</dt>
                                    <dd className="font-mono">{sensorStatus.lidar.device_info.hardware}</dd>
                                  </div>
                                )}
                                {sensorStatus.lidar.device_info.serial && (
                                  <div className="flex justify-between">
                                    <dt className="text-gray-500">Seriale:</dt>
                                    <dd className="font-mono text-xs truncate max-w-32" title={sensorStatus.lidar.device_info.serial}>
                                      {sensorStatus.lidar.device_info.serial.substring(0, 16)}...
                                    </dd>
                                  </div>
                                )}
                              </dl>
                            )}
                          </div>
                        )}

                        {/* Health Status */}
                        {sensorStatus.lidar.health && (
                          <div className={`mt-3 pt-3 border-t ${
                            sensorStatus.lidar.health.status === 'Good' ? 'border-green-200' :
                            sensorStatus.lidar.health.status === 'Scanning' ? 'border-blue-200' : 'border-yellow-200'
                          }`}>
                            <p className="text-xs font-medium mb-1">Stato Salute</p>
                            <div className={`flex items-center gap-2 ${
                              sensorStatus.lidar.health.status === 'Good' ? 'text-green-700' :
                              sensorStatus.lidar.health.status === 'Scanning' ? 'text-blue-700' :
                              sensorStatus.lidar.health.status === 'Warning' ? 'text-yellow-700' : 'text-red-700'
                            }`}>
                              {sensorStatus.lidar.health.status === 'Good' ? <CheckCircle size={14} /> :
                               sensorStatus.lidar.health.status === 'Scanning' ? <Activity size={14} /> :
                               sensorStatus.lidar.health.status === 'Warning' ? <AlertTriangle size={14} /> :
                               <XCircle size={14} />}
                              <span className="text-sm font-medium">{sensorStatus.lidar.health.status}</span>
                              {sensorStatus.lidar.health.error_code !== undefined && sensorStatus.lidar.health.error_code > 0 && (
                                <span className="text-xs text-gray-500">(err: {sensorStatus.lidar.health.error_code})</span>
                              )}
                            </div>
                            {sensorStatus.lidar.health.note && (
                              <p className="text-xs text-gray-500 mt-1 italic">{sensorStatus.lidar.health.note}</p>
                            )}
                          </div>
                        )}

                        {/* Error */}
                        {sensorStatus.lidar.error && (
                          <div className="mt-3 p-2 bg-red-100 rounded text-xs text-red-700">
                            <strong>Errore:</strong> {sensorStatus.lidar.error}
                          </div>
                        )}
                      </div>

                      {/* Radar Hardware */}
                      <div className={`p-4 rounded-lg border-2 ${
                        sensorStatus.radar.readable
                          ? 'bg-green-50 border-green-300'
                          : 'bg-red-50 border-red-300'
                      }`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Wifi size={24} className={
                              sensorStatus.radar.readable ? 'text-green-600' : 'text-red-600'
                            } />
                            <span className="font-semibold text-lg">Radar</span>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            sensorStatus.radar.readable
                              ? 'bg-green-200 text-green-800'
                              : 'bg-red-200 text-red-800'
                          }`}>
                            {sensorStatus.radar.readable ? '✓ FUNZIONANTE' : '✗ NON LEGGIBILE'}
                          </span>
                        </div>

                        <dl className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <dt className="text-gray-500">GPIO Pin:</dt>
                            <dd className="font-mono">GPIO {sensorStatus.radar.gpio_pin}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-500">GPIO configurato:</dt>
                            <dd>{sensorStatus.radar.gpio_configured ? '✓ Sì' : '✗ No'}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-gray-500">Leggibile:</dt>
                            <dd>{sensorStatus.radar.readable ? '✓ Sì' : '✗ No'}</dd>
                          </div>
                          {sensorStatus.radar.current_state && (
                            <div className="flex justify-between">
                              <dt className="text-gray-500">Stato attuale:</dt>
                              <dd className={`font-medium ${
                                sensorStatus.radar.current_state.includes('HIGH') ? 'text-green-600' : 'text-gray-600'
                              }`}>
                                {sensorStatus.radar.current_state}
                              </dd>
                            </div>
                          )}
                        </dl>

                        <div className="mt-4 p-3 bg-gray-100 rounded">
                          <p className="text-xs text-gray-600">
                            <strong>LD2410C</strong> - Sensore radar a 24GHz per rilevamento presenza.
                            Output digitale: HIGH quando rileva movimento/presenza.
                          </p>
                        </div>

                        {/* Error */}
                        {sensorStatus.radar.error && (
                          <div className="mt-3 p-2 bg-red-100 rounded text-xs text-red-700">
                            <strong>Errore:</strong> {sensorStatus.radar.error}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* System Status */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <CpuIcon size={18} />
                        Stato Sistema
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className={`text-2xl font-bold ${
                            sensorStatus.system.handler_running ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {sensorStatus.system.handler_running ? '✓' : '✗'}
                          </div>
                          <p className="text-xs text-gray-500">Handler</p>
                        </div>
                        <div className="text-center">
                          <div className={`text-2xl font-bold ${
                            sensorStatus.system.lidar_thread_alive ? 'text-green-600' : 'text-gray-400'
                          }`}>
                            {sensorStatus.system.lidar_thread_alive ? '✓' : '○'}
                          </div>
                          <p className="text-xs text-gray-500">Thread LIDAR</p>
                        </div>
                        <div className="text-center">
                          <div className={`text-2xl font-bold ${
                            sensorStatus.system.radar_thread_alive ? 'text-green-600' : 'text-gray-400'
                          }`}>
                            {sensorStatus.system.radar_thread_alive ? '✓' : '○'}
                          </div>
                          <p className="text-xs text-gray-500">Thread Radar</p>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-primary-600">
                            {sensorStatus.system.zones_configured}
                          </div>
                          <p className="text-xs text-gray-500">Zone Config</p>
                        </div>
                      </div>

                      {sensorStatus.system.error && (
                        <div className="mt-3 p-2 bg-yellow-100 rounded text-sm text-yellow-700">
                          <strong>Nota:</strong> {sensorStatus.system.error}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {isFetchingSensors ? (
                      <div className="flex items-center justify-center gap-2">
                        <LoadingSpinner size="sm" />
                        <span>Interrogazione sensori...</span>
                      </div>
                    ) : (
                      <div>
                        <Radar size={48} className="mx-auto text-gray-300 mb-3" />
                        <p>Clicca "Verifica Sensori" per controllare lo stato di LIDAR e Radar</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* System Actions */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Power size={20} className="text-primary-600" />
                  Azioni Sistema
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Change hostname */}
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <Server size={16} />
                      Nome Player (Hostname)
                    </h3>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newHostname}
                        onChange={(e) => setNewHostname(e.target.value)}
                        placeholder={systemInfo?.hostname || 'Nuovo hostname'}
                        className="input flex-1"
                        disabled={isExecuting}
                      />
                      <button
                        onClick={handleSetHostname}
                        disabled={isExecuting || !newHostname.trim()}
                        className="btn btn-primary"
                      >
                        Cambia
                      </button>
                    </div>
                  </div>

                  {/* Reboot */}
                  <div className="p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
                    <h3 className="font-medium text-yellow-800 mb-2 flex items-center gap-2">
                      <Power size={16} />
                      Riavvio Sistema
                    </h3>
                    <p className="text-sm text-yellow-700 mb-3">
                      Riavvia il Raspberry Pi. Il dispositivo sarà offline per alcuni minuti.
                    </p>
                    <button
                      onClick={handleReboot}
                      disabled={isExecuting}
                      className="btn bg-yellow-600 text-white hover:bg-yellow-700 w-full flex items-center justify-center gap-2"
                    >
                      <Power size={16} />
                      Riavvia Dispositivo
                    </button>
                  </div>

                  {/* Shutdown */}
                  <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
                    <h3 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                      <PowerOff size={16} />
                      Spegnimento
                    </h3>
                    <p className="text-sm text-red-700 mb-3">
                      Spegne il dispositivo. Sarà necessario accenderlo fisicamente.
                    </p>
                    <button
                      onClick={handleShutdown}
                      disabled={isExecuting}
                      className="btn bg-red-600 text-white hover:bg-red-700 w-full flex items-center justify-center gap-2"
                    >
                      <PowerOff size={16} />
                      Spegni Dispositivo
                    </button>
                  </div>
                </div>
              </div>

              {/* LED Test */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Lightbulb size={20} className="text-purple-600" />
                  Test LED WS2812B
                </h2>

                <div className="space-y-4">
                  {/* Range LED */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        LED Inizio
                      </label>
                      <input
                        type="number"
                        value={ledTestStart}
                        onChange={(e) => setLedTestStart(parseInt(e.target.value) || 0)}
                        className="input"
                        min={0}
                        max={299}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        LED Fine
                      </label>
                      <input
                        type="number"
                        value={ledTestEnd}
                        onChange={(e) => setLedTestEnd(parseInt(e.target.value) || 60)}
                        className="input"
                        min={1}
                        max={300}
                      />
                    </div>
                  </div>

                  {/* Color picker and presets */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Colore
                    </label>
                    <div className="flex items-center gap-3 flex-wrap">
                      <input
                        type="color"
                        value={ledTestColor}
                        onChange={(e) => setLedTestColor(e.target.value)}
                        className="w-12 h-10 rounded cursor-pointer border"
                      />
                      {/* Preset colors */}
                      {[
                        { color: '#ff0000', label: 'Rosso' },
                        { color: '#00ff00', label: 'Verde' },
                        { color: '#0000ff', label: 'Blu' },
                        { color: '#ffff00', label: 'Giallo' },
                        { color: '#ff00ff', label: 'Magenta' },
                        { color: '#00ffff', label: 'Ciano' },
                        { color: '#ffffff', label: 'Bianco' },
                      ].map((preset) => (
                        <button
                          key={preset.color}
                          type="button"
                          title={preset.label}
                          onClick={() => setLedTestColor(preset.color)}
                          className={`w-8 h-8 rounded border-2 transition-all ${
                            ledTestColor === preset.color
                              ? 'border-gray-800 scale-110'
                              : 'border-gray-300 hover:border-gray-500'
                          }`}
                          style={{ backgroundColor: preset.color }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => handleLedTest(ledTestColor)}
                      className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                    >
                      <Lightbulb size={16} />
                      Accendi LED
                    </button>
                    <button
                      onClick={handleLedOff}
                      className="btn btn-secondary flex items-center justify-center gap-2"
                    >
                      <PowerOff size={16} />
                      Spegni Tutti
                    </button>
                  </div>

                  {/* Quick test buttons */}
                  <div className="pt-2 border-t">
                    <p className="text-sm text-gray-500 mb-2">Test rapido:</p>
                    <div className="flex gap-2 flex-wrap">
                      {[
                        { color: '#ff0000', label: 'Rosso' },
                        { color: '#00ff00', label: 'Verde' },
                        { color: '#0000ff', label: 'Blu' },
                        { color: '#ffffff', label: 'Bianco' },
                      ].map((preset) => (
                        <button
                          key={preset.color}
                          onClick={() => handleLedTest(preset.color)}
                          className="btn btn-secondary text-sm py-1 px-3"
                          style={{ borderLeftColor: preset.color, borderLeftWidth: '4px' }}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* LIDAR Radar Trigger */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Radar size={20} className="text-blue-600" />
                  Controllo RPLIDAR
                </h2>

                <div className="space-y-4">
                  {/* Radar Trigger Toggle */}
                  <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-blue-900 flex items-center gap-2">
                          <Radar size={16} />
                          Attivazione RPLIDAR su Presenza Radar
                        </h3>
                        <p className="text-sm text-blue-700 mt-1">
                          Quando abilitato, l'RPLIDAR si attiva solo quando il radar rileva una presenza.
                          Utile per risparmiare energia e ridurre l'usura del motore.
                        </p>
                      </div>
                      <button
                        onClick={() => handleLidarRadarTriggerToggle(!lidarRadarTrigger)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          lidarRadarTrigger ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            lidarRadarTrigger ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <p className="text-xs text-blue-600">
                        <strong>Stato attuale:</strong>{' '}
                        {lidarRadarTrigger ? (
                          <span className="text-green-700">
                            Attivazione su presenza - RPLIDAR si accende solo quando il radar rileva qualcuno
                          </span>
                        ) : (
                          <span className="text-gray-600">
                            Scansione continua - RPLIDAR sempre attivo
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Timeout di spegnimento */}
                  {lidarRadarTrigger && (
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                        <Clock size={16} />
                        Timeout Spegnimento RPLIDAR
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        Tempo di attesa prima di spegnere l'RPLIDAR quando il radar non rileva più presenza.
                        Utile per evitare accensioni/spegnimenti frequenti.
                      </p>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          value={lidarRadarTimeout}
                          onChange={(e) => {
                            const val = Math.max(0, parseFloat(e.target.value) || 0)
                            handleLidarRadarTimeoutChange(val)
                          }}
                          className="input w-24 text-center"
                          min={0}
                          max={300}
                          step={0.5}
                        />
                        <span className="text-sm text-gray-500">secondi</span>
                        <div className="flex gap-2 ml-auto">
                          {[0, 2, 5, 10, 30].map((val) => (
                            <button
                              key={val}
                              onClick={() => handleLidarRadarTimeoutChange(val)}
                              className={`px-3 py-1 rounded text-sm transition-colors ${
                                lidarRadarTimeout === val
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {val === 0 ? 'Immediato' : `${val}s`}
                            </button>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {lidarRadarTimeout === 0
                          ? 'RPLIDAR si spegnerà immediatamente quando il radar non rileva più presenza.'
                          : `RPLIDAR resterà acceso per ${lidarRadarTimeout} secondi dopo la fine della presenza.`}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Configurazione GPIO Player */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Sliders size={20} className="text-purple-600" />
                    Configurazione GPIO Player
                  </h2>
                  <button
                    onClick={fetchPlayerSettings}
                    disabled={isFetchingSettings}
                    className="btn btn-secondary text-sm flex items-center gap-2"
                  >
                    {isFetchingSettings ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <Download size={14} />
                    )}
                    Carica Impostazioni
                  </button>
                </div>

                {!playerSettings && !isFetchingSettings && (
                  <div className="text-center py-8 text-gray-400">
                    <Sliders size={48} className="mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Clicca "Carica Impostazioni" per leggere la configurazione GPIO dal player.</p>
                  </div>
                )}

                {isFetchingSettings && (
                  <div className="text-center py-8">
                    <RefreshCw size={24} className="mx-auto mb-2 animate-spin text-purple-500" />
                    <p className="text-sm text-gray-500">Lettura impostazioni dal player...</p>
                  </div>
                )}

                {playerSettings && !isFetchingSettings && (
                  <div className="space-y-5">
                    {/* LED 12V */}
                    <div className="p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
                      <h3 className="font-medium text-yellow-900 mb-3 flex items-center gap-2">
                        <Lightbulb size={16} />
                        LED 12V (MOSFET/Relè)
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Pin GPIO</label>
                          <input
                            type="number"
                            min={0}
                            max={27}
                            value={getSettingValue('GPIO_LED_12V_PIN') ?? ''}
                            onChange={(e) => updateSetting('GPIO_LED_12V_PIN', parseInt(e.target.value) || 0)}
                            className="input w-full"
                          />
                          <p className="text-xs text-gray-500 mt-1">BCM 0-27</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Modalità</label>
                          <div className="flex items-center gap-3 mt-2">
                            <button
                              onClick={() => updateSetting('GPIO_LED_12V_INVERTED', !getSettingValue('GPIO_LED_12V_INVERTED'))}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 ${
                                getSettingValue('GPIO_LED_12V_INVERTED') ? 'bg-yellow-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  getSettingValue('GPIO_LED_12V_INVERTED') ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                            <span className="text-sm text-gray-700">
                              {getSettingValue('GPIO_LED_12V_INVERTED') ? 'Relè (invertito)' : 'MOSFET (diretto)'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* WS2812B */}
                    <div className="p-4 border border-green-200 bg-green-50 rounded-lg">
                      <h3 className="font-medium text-green-900 mb-3 flex items-center gap-2">
                        <Zap size={16} />
                        WS2812B (Strip LED)
                      </h3>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Pin GPIO (PWM)</label>
                          <select
                            value={getSettingValue('GPIO_WS2812_PIN') ?? 12}
                            onChange={(e) => updateSetting('GPIO_WS2812_PIN', parseInt(e.target.value))}
                            className="input w-full"
                          >
                            {[12, 13, 18, 19].map((pin) => (
                              <option key={pin} value={pin}>GPIO {pin}</option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">Solo pin PWM</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Numero LED</label>
                          <input
                            type="number"
                            min={1}
                            max={1000}
                            value={getSettingValue('GPIO_WS2812_COUNT') ?? ''}
                            onChange={(e) => updateSetting('GPIO_WS2812_COUNT', Math.max(1, Math.min(1000, parseInt(e.target.value) || 1)))}
                            className="input w-full"
                          />
                          <p className="text-xs text-gray-500 mt-1">1-1000</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Luminosità: {getSettingValue('GPIO_WS2812_BRIGHTNESS') ?? 255}
                          </label>
                          <input
                            type="range"
                            min={0}
                            max={255}
                            value={getSettingValue('GPIO_WS2812_BRIGHTNESS') ?? 255}
                            onChange={(e) => updateSetting('GPIO_WS2812_BRIGHTNESS', parseInt(e.target.value))}
                            className="w-full mt-2"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>0</span>
                            <span>255</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Radar */}
                    <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
                      <h3 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                        <Radar size={16} />
                        Radar (Sensore presenza)
                      </h3>
                      <div className="max-w-xs">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pin GPIO</label>
                        <input
                          type="number"
                          min={0}
                          max={27}
                          value={getSettingValue('GPIO_RADAR_PIN') ?? ''}
                          onChange={(e) => updateSetting('GPIO_RADAR_PIN', parseInt(e.target.value) || 0)}
                          className="input w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">BCM 0-27</p>
                      </div>
                    </div>

                    {/* Banner modifiche non salvate */}
                    {hasSettingsChanges && (
                      <div className="p-3 bg-yellow-100 border border-yellow-300 rounded-lg flex items-center gap-2">
                        <AlertTriangle size={16} className="text-yellow-700 flex-shrink-0" />
                        <span className="text-sm text-yellow-800 font-medium">
                          Modifiche non salvate: {Object.keys(editedSettings).join(', ')}
                        </span>
                      </div>
                    )}

                    {/* Banner risultato salvataggio */}
                    {settingsSaveResult && (
                      <div className={`p-3 rounded-lg flex items-center gap-2 ${
                        settingsSaveResult.success
                          ? 'bg-green-100 border border-green-300'
                          : 'bg-red-100 border border-red-300'
                      }`}>
                        {settingsSaveResult.success ? (
                          <CheckCircle size={16} className="text-green-700 flex-shrink-0" />
                        ) : (
                          <XCircle size={16} className="text-red-700 flex-shrink-0" />
                        )}
                        <span className={`text-sm font-medium ${
                          settingsSaveResult.success ? 'text-green-800' : 'text-red-800'
                        }`}>
                          {settingsSaveResult.message}
                        </span>
                      </div>
                    )}

                    {/* Bottoni salva */}
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => handleSavePlayerSettings(false)}
                        disabled={!hasSettingsChanges || isSavingSettings}
                        className="btn btn-secondary flex items-center gap-2"
                      >
                        <Save size={14} />
                        Salva
                      </button>
                      <button
                        onClick={() => handleSavePlayerSettings(true)}
                        disabled={!hasSettingsChanges || isSavingSettings}
                        className="btn btn-primary flex items-center gap-2"
                      >
                        {isSavingSettings ? (
                          <RefreshCw size={14} className="animate-spin" />
                        ) : (
                          <RotateCcw size={14} />
                        )}
                        Salva e Riavvia
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Terminal */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Terminal size={20} className="text-primary-600" />
                    Terminale Remoto
                  </h2>
                  <div className="flex items-center gap-2">
                    {terminalConnected ? (
                      <>
                        <span className="text-sm text-green-600 flex items-center gap-1">
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          Connesso
                        </span>
                        <button
                          onClick={disconnectTerminal}
                          className="btn btn-secondary text-sm flex items-center gap-2"
                        >
                          <PowerOff size={14} />
                          Disconnetti
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={connectTerminal}
                        className="btn btn-primary text-sm flex items-center gap-2"
                      >
                        <Power size={14} />
                        Connetti
                      </button>
                    )}
                  </div>
                </div>

                {/* Terminal container */}
                <div
                  ref={terminalRef}
                  className="rounded-lg overflow-hidden"
                  style={{
                    height: '400px',
                    backgroundColor: '#1a1a2e',
                    padding: terminalConnected ? '8px' : '0'
                  }}
                />

                {!terminalConnected && (
                  <p className="text-sm text-gray-500 mt-4 text-center">
                    Clicca "Connetti" per aprire una sessione terminale interattiva sul dispositivo.
                  </p>
                )}
              </div>

              {/* File Manager */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FolderOpen size={20} className="text-primary-600" />
                  File Manager
                </h2>
                <FileManager
                  deviceId={device?.device_id || ''}
                  isOnline={device?.status === 'online'}
                />
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="space-y-4">
          {/* Header con stato connessione */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ScrollText size={24} className="text-primary-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Log Connessione</h2>
                  <p className="text-sm text-gray-500">
                    Monitora la comunicazione tra il dispositivo e il server
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {/* Stato WebSocket */}
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                  isWsConnected
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {isWsConnected ? <CheckCircle size={16} /> : <XCircle size={16} />}
                  WebSocket: {isWsConnected ? 'Connesso' : 'Disconnesso'}
                </div>
                {/* Stato dispositivo */}
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                  device?.status === 'online'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {device?.status === 'online' ? <Wifi size={16} /> : <WifiOff size={16} />}
                  Device: {device?.status || 'unknown'}
                </div>
              </div>
            </div>
          </div>

          {/* Controlli */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={autoScroll}
                    onChange={(e) => setAutoScroll(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Auto-scroll
                </label>
                <span className="text-sm text-gray-500">
                  {connectionLogs.length} messaggi
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    logOutgoing('Test ping WebSocket', { type: 'ping', device_id: deviceId })
                    wsService.send({ type: 'ping', device_id: deviceId })
                  }}
                  className="btn-secondary text-sm"
                >
                  Test Ping
                </button>
                <button
                  onClick={() => {
                    logOutgoing('Richiesta config update', { device_id: deviceId })
                    wsService.notifyConfigUpdate(deviceId!)
                  }}
                  className="btn-secondary text-sm"
                  disabled={!deviceId}
                >
                  Invia Config Update
                </button>
                <button
                  onClick={() => setConnectionLogs([])}
                  className="btn-secondary text-sm text-red-600 hover:text-red-700"
                >
                  Pulisci Log
                </button>
              </div>
            </div>
          </div>

          {/* Log entries */}
          <div className="bg-gray-900 rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-300">Console Log</span>
              <div className="flex items-center gap-4 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <ArrowDownCircle size={12} className="text-blue-400" /> Incoming
                </span>
                <span className="flex items-center gap-1">
                  <ArrowUpCircle size={12} className="text-purple-400" /> Outgoing
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle size={12} className="text-green-400" /> Success
                </span>
                <span className="flex items-center gap-1">
                  <XCircle size={12} className="text-red-400" /> Error
                </span>
              </div>
            </div>
            <div className="h-96 overflow-y-auto p-4 font-mono text-sm">
              {connectionLogs.length === 0 ? (
                <div className="text-gray-500 text-center py-8">
                  Nessun log. I messaggi appariranno qui quando il dispositivo comunica con il server.
                </div>
              ) : (
                connectionLogs.map((log) => (
                  <div
                    key={log.id}
                    className={`py-1 px-2 rounded mb-1 flex items-start gap-2 ${
                      log.type === 'error' ? 'bg-red-900/30 text-red-300' :
                      log.type === 'success' ? 'bg-green-900/30 text-green-300' :
                      log.type === 'outgoing' ? 'bg-purple-900/30 text-purple-300' :
                      log.type === 'incoming' ? 'bg-blue-900/30 text-blue-300' :
                      'bg-gray-800/50 text-gray-300'
                    }`}
                  >
                    <span className="text-gray-500 shrink-0">
                      {log.timestamp.toLocaleTimeString('it-IT', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}.{String(log.timestamp.getMilliseconds()).padStart(3, '0')}
                    </span>
                    <span className="shrink-0">
                      {log.type === 'error' && <XCircle size={14} className="text-red-400" />}
                      {log.type === 'success' && <CheckCircle size={14} className="text-green-400" />}
                      {log.type === 'outgoing' && <ArrowUpCircle size={14} className="text-purple-400" />}
                      {log.type === 'incoming' && <ArrowDownCircle size={14} className="text-blue-400" />}
                      {log.type === 'info' && <Info size={14} className="text-gray-400" />}
                    </span>
                    <span className="flex-1">{log.message}</span>
                    {log.data !== undefined && (
                      <button
                        onClick={() => {
                          console.log('Log data:', log.data)
                          alert(JSON.stringify(log.data, null, 2))
                        }}
                        className="text-gray-500 hover:text-gray-300 shrink-0"
                        title="Mostra dati completi"
                      >
                        <Eye size={14} />
                      </button>
                    )}
                  </div>
                ))
              )}
              <div ref={logsEndRef} />
            </div>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 rounded-xl p-4 flex items-start gap-3">
            <Info size={20} className="text-blue-600 shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Come funziona</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li><strong>Incoming</strong>: Messaggi ricevuti dal dispositivo (eventi, heartbeat, risposte)</li>
                <li><strong>Outgoing</strong>: Messaggi inviati al dispositivo (comandi, config update)</li>
                <li><strong>Success</strong>: Connessione stabilita o operazione completata</li>
                <li><strong>Error</strong>: Disconnessione o errore nella comunicazione</li>
              </ul>
              <p className="mt-2">
                Se non vedi messaggi "Incoming", il dispositivo potrebbe non essere connesso o non sta inviando dati.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'guide' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3 mb-4">
              <BookOpen size={24} className="text-primary-600" />
              Guida Collegamento Hardware
            </h2>
            <p className="text-gray-600">
              Questa guida descrive come collegare i sensori e i LED al Raspberry Pi per il sistema IntelliExpo.
            </p>
          </div>

          {/* RPLIDAR C1 */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Radar size={20} className="text-blue-600" />
              RPLIDAR C1 (Sensore LIDAR)
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              {/* USB Connection */}
              <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                  <Cable size={16} />
                  Collegamento via USB (Consigliato)
                </h4>
                <div className="text-sm text-blue-800 space-y-2">
                  <p>Il metodo più semplice: collega il RPLIDAR C1 direttamente a una porta USB del Raspberry Pi.</p>
                  <div className="bg-white rounded p-3 font-mono text-xs">
                    <p><strong>Porta:</strong> /dev/ttyUSB0</p>
                    <p><strong>Baudrate:</strong> 460800</p>
                  </div>
                  <p className="text-blue-600 text-xs mt-2">
                    Nota: Il dispositivo potrebbe apparire come /dev/ttyUSB1 se altre periferiche USB sono collegate.
                  </p>
                </div>
              </div>

              {/* GPIO UART Connection */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Cable size={16} />
                  Collegamento via GPIO UART
                </h4>
                <div className="text-sm text-gray-700 space-y-2">
                  <p>Per installazioni permanenti, usa la porta UART del GPIO:</p>
                  <table className="w-full text-xs mt-2">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1">RPLIDAR</th>
                        <th className="text-left py-1">Raspberry Pi</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-1">TX (Giallo)</td>
                        <td className="py-1">GPIO 15 (RXD) - Pin 10</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-1">RX (Verde)</td>
                        <td className="py-1">GPIO 14 (TXD) - Pin 8</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-1">GND (Nero)</td>
                        <td className="py-1">GND - Pin 6</td>
                      </tr>
                      <tr>
                        <td className="py-1">5V (Rosso)</td>
                        <td className="py-1">5V - Pin 2 o 4</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-3 text-xs">
                    <strong>Importante:</strong> Disabilita Bluetooth per liberare UART:<br/>
                    <code className="bg-yellow-100 px-1">sudo nano /boot/config.txt</code><br/>
                    Aggiungi: <code className="bg-yellow-100 px-1">dtoverlay=disable-bt</code>
                  </div>
                </div>
              </div>
            </div>

            {/* Config reference */}
            <div className="mt-4 p-3 bg-gray-100 rounded-lg text-xs font-mono">
              <p className="text-gray-500 mb-1"># settings.py</p>
              <p>GPIO_LIDAR_UART = "/dev/ttyUSB0"  <span className="text-gray-400"># o /dev/ttyAMA0 per GPIO</span></p>
              <p>GPIO_LIDAR_BAUD = 460800</p>
              <p>GPIO_LIDAR_TYPE = "rplidar_c1"</p>
            </div>
          </div>

          {/* Radar Sensor */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Zap size={20} className="text-green-600" />
              Sensore Radar (Presenza)
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Schema di Collegamento</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Radar</th>
                      <th className="text-left py-2">Raspberry Pi</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2">VCC</td>
                      <td className="py-2">5V - Pin 4</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2">OUT (segnale)</td>
                      <td className="py-2 font-medium text-green-700">GPIO 21 - Pin 13</td>
                    </tr>
                    <tr>
                      <td className="py-2">GND</td>
                      <td className="py-2">GND - Pin 9</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">Sensori Compatibili</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    <span><strong>RCWL-0516</strong> - Radar a microonde, buona sensibilità</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    <span><strong>HLK-LD2410</strong> - Alta precisione, configurabile</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    <span><strong>HC-SR501</strong> - PIR infrarosso (alternativa economica)</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-gray-100 rounded-lg text-xs font-mono">
                  <p className="text-gray-500 mb-1"># settings.py</p>
                  <p>GPIO_RADAR_PIN = 21</p>
                </div>
              </div>
            </div>
          </div>

          {/* LED 12V */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Lightbulb size={20} className="text-yellow-600" />
              LED 12V (via MOSFET o Relè)
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Schema di Collegamento</h4>
                <div className="border rounded-lg p-4 bg-gray-50">
                  <pre className="text-xs font-mono text-gray-700 whitespace-pre">
{`Raspberry Pi          MOSFET (IRLZ44N)      LED Strip
                         ┌───────┐
GPIO 24 (Pin 18) ────────┤ Gate  │
                         │       │
GND (Pin 20) ────────────┤Source │
                         │       │
12V Power (+) ───────────┼───────┼──── LED (+)
                         │       │
                         │ Drain ├──── LED (-)
                         └───────┘
12V Power (-) ───────────────────────── GND comune

Alternativa con Relè:
GPIO 24 (Pin 18) ────── IN (segnale)
5V     (Pin 2/4) ────── VCC
GND    (Pin 6)   ────── GND`}
                  </pre>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">Componenti Necessari</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-500 mt-1">•</span>
                    <span><strong>MOSFET IRLZ44N</strong> - Logic level, fino a 50A</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-500 mt-1">•</span>
                    <span><strong>Resistenza 10kΩ</strong> - Pull-down Gate-Source (opzionale)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-500 mt-1">•</span>
                    <span><strong>Alimentatore 12V</strong> - Adeguato alla potenza LED</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs">
                  <strong>Attenzione:</strong> Il GND del Raspberry Pi deve essere collegato al GND dell'alimentatore 12V (massa comune).
                </div>
                <div className="mt-3 p-3 bg-gray-100 rounded-lg text-xs font-mono">
                  <p className="text-gray-500 mb-1"># settings.py</p>
                  <p>GPIO_LED_12V_PIN = 24</p>
                  <p>GPIO_LED_12V_INVERTED = True  <span className="text-gray-400"># True=relè, False=MOSFET</span></p>
                </div>
              </div>
            </div>
          </div>

          {/* WS2812B LED Strip */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Zap size={20} className="text-purple-600" />
              LED WS2812B (Strip RGB Indirizzabile)
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Schema di Collegamento</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">WS2812B</th>
                      <th className="text-left py-2">Raspberry Pi</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2">5V (rosso)</td>
                      <td className="py-2">5V esterno (alimentatore)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-medium text-purple-700">DIN (verde)</td>
                      <td className="py-2 font-medium text-purple-700">GPIO 12 - Pin 32</td>
                    </tr>
                    <tr>
                      <td className="py-2">GND (bianco)</td>
                      <td className="py-2">GND - Pin 34 + GND alimentatore</td>
                    </tr>
                  </tbody>
                </table>
                <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg text-xs">
                  <strong>Level Shifter:</strong> I WS2812B funzionano a 5V, ma spesso accettano anche 3.3V dal GPIO. Per maggiore affidabilità, usa un level shifter 3.3V → 5V.
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">Pin PWM Compatibili</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-1">•</span>
                    <span><strong>GPIO 12</strong> (PWM0) - Consigliato</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-1">•</span>
                    <span><strong>GPIO 18</strong> (PWM0) - Alternativa (conflitto con audio)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-1">•</span>
                    <span><strong>GPIO 13</strong> (PWM1) - Alternativa</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-gray-100 rounded-lg text-xs font-mono">
                  <p className="text-gray-500 mb-1"># settings.py</p>
                  <p>GPIO_WS2812_PIN = 12</p>
                  <p>GPIO_WS2812_COUNT = 60  <span className="text-gray-400"># numero LED</span></p>
                  <p>GPIO_WS2812_BRIGHTNESS = 255</p>
                </div>
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs">
                  <strong>Alimentazione:</strong> Ogni LED consuma ~60mA a piena luminosità bianca. Per 60 LED servono ~3.6A. Usa un alimentatore 5V adeguato.
                </div>
              </div>
            </div>
          </div>

          {/* Raspberry Pi Pinout Reference */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <CpuIcon size={20} className="text-red-600" />
              Riepilogo GPIO Raspberry Pi
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-2 px-3">Componente</th>
                    <th className="text-left py-2 px-3">GPIO (BCM)</th>
                    <th className="text-left py-2 px-3">Pin Fisico</th>
                    <th className="text-left py-2 px-3">Note</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 px-3 font-medium">LIDAR TX → RPi RX</td>
                    <td className="py-2 px-3">GPIO 15 (RXD)</td>
                    <td className="py-2 px-3">Pin 10</td>
                    <td className="py-2 px-3 text-gray-500">Solo se via GPIO UART</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-3 font-medium">LIDAR RX → RPi TX</td>
                    <td className="py-2 px-3">GPIO 14 (TXD)</td>
                    <td className="py-2 px-3">Pin 8</td>
                    <td className="py-2 px-3 text-gray-500">Solo se via GPIO UART</td>
                  </tr>
                  <tr className="border-b bg-green-50">
                    <td className="py-2 px-3 font-medium">Radar OUT</td>
                    <td className="py-2 px-3 text-green-700">GPIO 17</td>
                    <td className="py-2 px-3">Pin 11</td>
                    <td className="py-2 px-3 text-gray-500">Segnale presenza</td>
                  </tr>
                  <tr className="border-b bg-yellow-50">
                    <td className="py-2 px-3 font-medium">LED 12V (MOSFET/Relè)</td>
                    <td className="py-2 px-3 text-yellow-700">GPIO 24</td>
                    <td className="py-2 px-3">Pin 18</td>
                    <td className="py-2 px-3 text-gray-500">Gate MOSFET o segnale Relè</td>
                  </tr>
                  <tr className="bg-purple-50">
                    <td className="py-2 px-3 font-medium">WS2812B DIN</td>
                    <td className="py-2 px-3 text-purple-700">GPIO 12</td>
                    <td className="py-2 px-3">Pin 32</td>
                    <td className="py-2 px-3 text-gray-500">PWM0, dati LED</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Modalità Operativa</h4>
              <p className="text-sm text-blue-800">
                Per utilizzare GPIO diretto (senza Pico), avvia il player con:<br/>
                <code className="bg-blue-100 px-2 py-1 rounded mt-1 inline-block">python3 main.py --direct-gpio</code>
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="bg-white rounded-xl shadow-sm p-6 max-w-xl">
          <h2 className="text-lg font-semibold mb-6">Impostazioni Dispositivo</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Dispositivo
              </label>
              <input
                type="text"
                value={deviceName || device.name}
                onChange={(e) => setDeviceName(e.target.value)}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrizione
              </label>
              <textarea
                value={deviceDescription || device.description || ''}
                onChange={(e) => setDeviceDescription(e.target.value)}
                className="input"
                rows={3}
              />
            </div>

            <div className="pt-4">
              <button
                onClick={handleSaveSettings}
                className="btn-primary"
                disabled={updateDevice.isPending}
              >
                {updateDevice.isPending ? 'Salvataggio...' : 'Salva Modifiche'}
              </button>
            </div>
          </div>

          {/* Device info */}
          <div className="mt-8 pt-6 border-t">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Informazioni</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Device ID:</dt>
                <dd className="font-mono">{device.device_id}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Versione Software:</dt>
                <dd className={`font-mono ${device.software_version ? 'text-green-600' : 'text-gray-400'}`}>
                  {device.software_version || 'N/A'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Ultimo IP:</dt>
                <dd className="font-mono">{device.last_ip || '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Creato:</dt>
                <dd>{new Date(device.created_at).toLocaleString('it-IT')}</dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {/* Zone Modal */}
      <Modal
        isOpen={zoneModalOpen}
        onClose={() => {
          setZoneModalOpen(false)
          setEditingZone(null)
        }}
        title={editingZone ? 'Modifica Zona' : 'Nuova Zona'}
        size="lg"
      >
        <ZoneForm
          zone={editingZone}
          onSave={handleSaveZone}
          onCancel={() => {
            setZoneModalOpen(false)
            setEditingZone(null)
          }}
          nextZoneNumber={zones.length + 1}
        />
      </Modal>

      {/* Trigger Modal */}
      <Modal
        isOpen={triggerModalOpen}
        onClose={() => {
          setTriggerModalOpen(false)
          setEditingTrigger(null)
        }}
        title={editingTrigger ? 'Modifica Trigger' : 'Nuovo Trigger'}
        size="lg"
      >
        <TriggerForm
          trigger={editingTrigger}
          zones={zones}
          mediaList={mediaList}
          onSave={handleSaveTrigger}
          onCancel={() => {
            setTriggerModalOpen(false)
            setEditingTrigger(null)
          }}
        />
      </Modal>
    </div>
  )
}
